import i18n from '@/core/i18n';
import { normalizeColorHex } from '@/lib/utils';
import { mapClientStatusFromDTO } from '@/modules/clients/mappers/client-status.mapper';
import { ApiErrorCode, createApiError } from '@/modules/common/api/errors';
import { mapCurrencyFromDTO } from '@/modules/common/mappers/currency.mapper';
import { mapIntegrationProvidersFromDTO } from '@/modules/common/mappers/integration.mapper';
import { CustomAttribute } from '@/modules/common/types/custom-attribute';
import { ProjectFormData } from '@/modules/projects/types';
import { CreateProjectRequest } from '@/modules/projects/types/dto/request/create-project-request';
import { UpdateProjectFullRequest } from '@/modules/projects/types/dto/request/update-project-request';
import { ProjectFullResponse } from '@/modules/projects/types/dto/response/project-full-response';
import { ProjectSummaryResponse } from '@/modules/projects/types/dto/response/project-summary-response';
import { MapProjectToPatchRequestParams } from '@/modules/projects/types/patch-project-change-params';
import { mapOpsiumUserShortFromDTO } from '@/modules/team/mappers/opsium-user-short.mapper';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';
import { StringUtils } from '@/utils/string/string-utils';

import { DEFAULT_PROJECT_COLOR_CSS_VAR } from '../constants/project-color';
import { Project, ProjectListItem } from '../types/project';
import { mapProjectBillingModelFromDTO } from './project-billing-model.mapper';
import {
  mapPartialProjectBillingToDTO,
  mapProjectBillingFromResponse,
  mapProjectBillingToDTO,
} from './project-billing.mapper';
import {
  mapPartialProjectInfoToDTO,
  mapProjectInfoFromResponse,
  mapProjectInfoToDTO,
} from './project-info.mapper';
import {
  mapIntegrationsFromResponse,
  mapProjectIntegrationsToDTO,
} from './project-integration.mapper';
import { mapProjectStatusFromDTO } from './project-status.mapper';

/**
 * Maps API project summary response to frontend project list item.
 * Handles conversion of API DTOs to application enums.
 */
export const mapProjectSummary = (response: ProjectSummaryResponse): ProjectListItem => {
  return {
    id: response.id,
    name: StringUtils.nullIfEmpty(response.name) ?? i18n.t('common:noNameProject'),
    logoUrl: response.logoUrl,
    colorHex: normalizeColorHex(response.colorHex) ?? DEFAULT_PROJECT_COLOR_CSS_VAR,
    status: mapProjectStatusFromDTO(response.status),
    // Convert probability from 0-1 to 0-100
    tentativeProbability:
      response.tentativeProbability !== null && response.tentativeProbability !== undefined
        ? response.tentativeProbability * 100
        : response.tentativeProbability,
    billingModel: response.billingModel
      ? mapProjectBillingModelFromDTO(response.billingModel)
      : null,
    managers: (response.managers ?? []).map(mapOpsiumUserShortFromDTO),
    defaultCurrency: mapCurrencyFromDTO(response.defaultCurrency),
    projectStartDate: response.projectStartDate,
    projectEndDate: response.projectEndDate,
    nextArchiveDate: response.nextArchiveDate,
    nextUnarchiveDate: response.nextUnarchiveDate,
    industries: response.industries,
    connectedIntegrations: mapIntegrationProvidersFromDTO(response.connectedIntegrations),
    client: response.client || undefined,
  };
};

/**
 * Maps an array of project summary responses.
 */
export const mapProjectSummaries = (responses: ProjectSummaryResponse[]): ProjectListItem[] => {
  return responses.map(mapProjectSummary);
};

/**
 * Maps full project API response DTO to application domain model.
 * Delegates to specialized mappers for each section.
 */
export const mapProjectDetailsFromResponse = (response: ProjectFullResponse): Project => {
  return {
    id: response.id,
    name: response.info.name,
    client: response.info.client
      ? {
          id: response.info.client.id,
          name: response.info.client.name,
          logoUrl: response.info.client.logoUrl,
          status: mapClientStatusFromDTO(response.info.client.status),
          isDeleted: response.info.client.isDeleted,
        }
      : undefined,
    info: mapProjectInfoFromResponse(response.info),
    billing: mapProjectBillingFromResponse(response.billing),
    integrations: {
      integrations: mapIntegrationsFromResponse(response.integrations.integrations),
    },
    nextArchiveDate: response.nextArchiveDate,
    nextUnarchiveDate: response.nextUnarchiveDate,
  };
};

/**
 * Maps ProjectFormData to CreateProjectRequest DTO for create operations.
 * Delegates to specialized mappers for each section.
 */
export const mapProjectFormDataToCreateRequest = (
  data: ProjectFormData,
  availableAttributes: CustomAttribute[] = []
): CreateProjectRequest => {
  const integrations = mapProjectIntegrationsToDTO(data.integrations);
  const clientId = data.clientId ? parseInt(data.clientId, 10) : undefined;

  return {
    clientId,
    info: mapProjectInfoToDTO(data, availableAttributes),
    billing: mapProjectBillingToDTO(data),
    ...(integrations && integrations.length > 0 && { integrations: { integrations } }),
  };
};

/**
 * Maps ProjectFormData to PatchProjectFullRequest DTO for edit operations.
 * Delegates to specialized mappers for each section.
 * Only includes fields that are defined (supports partial updates).
 */
export const mapProjectFormDataToPatchRequest = ({
  data,
  projectId,
  effectiveStartDate = new Date(),
  currentStatus,
  availableAttributes = [],
  effectiveEndDate,
}: MapProjectToPatchRequestParams): UpdateProjectFullRequest => {
  const id = typeof projectId === 'number' ? projectId : Number.parseInt(String(projectId), 10);

  if (Number.isNaN(id)) {
    const originalError = new Error(
      `Invalid projectId provided to mapProjectFormDataToPatchRequest: ${projectId}`
    );
    throw createApiError(
      ApiErrorCode.PROJECT_UPDATE_FAILED,
      originalError,
      'Invalid project identifier'
    );
  }

  const info = mapPartialProjectInfoToDTO(data, availableAttributes, currentStatus);
  const billing = mapPartialProjectBillingToDTO(data);

  const request: UpdateProjectFullRequest = {
    projectId: id,
    effectiveStartDate: normalizeToStartOfDayUTC(effectiveStartDate).toISOString(),
  };

  // Add effectiveEndDate if provided (can be null for infinite/ongoing)
  if (effectiveEndDate !== undefined) {
    request.effectiveEndDate = effectiveEndDate
      ? normalizeToStartOfDayUTC(effectiveEndDate).toISOString()
      : null;
  }

  if (Object.keys(info).length > 0) request.info = info;
  if (Object.keys(billing).length > 0) request.billing = billing;

  return request;
};
